import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { filter, finalize, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '@app/sharedModule/components/confirmation-dialog/confirmation-dialog.component';
import { ToastService } from '@app/storybook/services/toast/toast.service';
import { SmartCartService, StandaloneWidgetItem } from '../../smart-cart.service';

type StandaloneMode = 'live' | 'disabled';

interface StandaloneWidgetRow {
  widget: StandaloneWidgetItem;
  subtitle: string;
  stateControl: FormControl<StandaloneMode>;
}

enum Actions {
  EDIT = 'edit',
  DUPLICATE = 'duplicate',
  DELETE = 'delete',
}

const ActionOptions = [
  { label: 'Edit', value: Actions.EDIT },
  { label: 'Duplicate', value: Actions.DUPLICATE },
  { label: 'Delete', value: Actions.DELETE },
];

const SELECTING_METHOD_LABELS: Record<string, string> = {
  toggle: 'Toggle',
  checkbox: 'Checkbox',
  quick_checkout: 'Quick checkout',
};

@Component({
  selector: 'standalone-widgets-list',
  templateUrl: './standalone-widgets-list.component.html',
  styleUrls: ['./standalone-widgets-list.component.scss'],
  standalone: false,
})
export class StandaloneWidgetsListComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  // Emits at the start of each (re)load so the previous rows' toggle subscriptions complete — otherwise
  // duplicate()→load() leaves orphaned valueChanges subscriptions alive until the component is destroyed.
  private readonly reload$ = new Subject<void>();

  rows: StandaloneWidgetRow[] = [];
  loading = false;
  // Row being duplicated (server-side provisioning is slow) — drives the spinner + blocks concurrent duplicates.
  duplicatingId: number | null = null;
  actionOptions = ActionOptions;
  readonly Actions = Actions;

  constructor(
    private smartCartService: SmartCartService,
    private toastService: ToastService,
    private dialog: MatDialog,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  onNew(): void {
    this.router.navigate(['/engage/smart-cart/standalone/new']);
  }

  onActionOptionSelect(optionValue: Actions, id: number): void {
    switch (optionValue) {
      case Actions.EDIT:
        this.router.navigate(['/engage/smart-cart/standalone', id]);
        break;
      case Actions.DELETE:
        this.confirmDelete(id);
        break;
      case Actions.DUPLICATE:
        this.duplicate(id);
        break;
    }
  }

  private duplicate(id: number): void {
    if (this.duplicatingId !== null) return;
    this.duplicatingId = id;
    this.smartCartService
      .duplicateStandaloneWidget(id)
      .pipe(
        finalize(() => (this.duplicatingId = null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.toastService.showToast({ mode: 'success', message: 'Standalone widget duplicated' });
          this.load();
        },
        error: (err) => {
          console.error('Failed to duplicate standalone widget:', err);
          this.toastService.showToast({ mode: 'danger', message: 'Failed to duplicate standalone widget' });
        },
      });
  }

  private confirmDelete(id: number): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: 'Delete standalone widget',
          description: 'Are you sure you want to delete this standalone widget?',
          confirmText: 'Delete',
          cancelText: 'Cancel',
        },
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.smartCartService.deleteStandaloneWidget(id)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.rows = this.rows.filter((row) => row.widget.id !== id);
          this.toastService.showToast({ mode: 'success', message: 'Standalone widget deleted' });
        },
        error: (err) => {
          console.error('Failed to delete standalone widget:', err);
          this.toastService.showToast({ mode: 'danger', message: 'Failed to delete standalone widget' });
        },
      });
  }

  private load(): void {
    // Tear down the prior rows' toggle subscriptions before rebuilding.
    this.reload$.next();
    this.loading = true;
    this.smartCartService
      .listStandaloneWidgets()
      .pipe(
        finalize(() => (this.loading = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (widgets) => {
          this.rows = (widgets ?? []).map((widget) => this.buildRow(widget));
        },
        error: (err) => {
          console.error('Error loading standalone widgets:', err);
          this.toastService.showToast({ mode: 'danger', message: 'Failed to load standalone widgets' });
          this.rows = [];
        },
      });
  }

  private buildRow(widget: StandaloneWidgetItem): StandaloneWidgetRow {
    const initial: StandaloneMode = widget.isLive ? 'live' : 'disabled';
    const stateControl = new FormControl<StandaloneMode>(initial, { nonNullable: true });
    const row: StandaloneWidgetRow = { widget, subtitle: this.buildSubtitle(widget), stateControl };

    stateControl.valueChanges
      .pipe(takeUntil(this.reload$), takeUntilDestroyed(this.destroyRef))
      .subscribe((nextValue) => {
        const currentValue: StandaloneMode = row.widget.isLive ? 'live' : 'disabled';
        if (nextValue === currentValue) return;
        this.confirmStateChange(row, nextValue, currentValue);
      });

    return row;
  }

  private buildSubtitle(widget: StandaloneWidgetItem): string {
    const selectingMethod = (widget.settings?.['selectingMethod'] as { type?: string } | undefined)?.type;
    const methodLabel = selectingMethod ? SELECTING_METHOD_LABELS[selectingMethod] : null;
    return methodLabel ? `Order Protection · ${methodLabel}` : 'Order Protection';
  }

  private confirmStateChange(
    row: StandaloneWidgetRow,
    requestedValue: StandaloneMode,
    previousValue: StandaloneMode,
  ): void {
    const isActivating = requestedValue === 'live';
    const description = isActivating
      ? 'This standalone widget will go live on the storefront where it is embedded.'
      : 'This standalone widget will be turned off.';
    const confirmText = isActivating ? 'Go Live' : 'Confirm';

    this.dialog
      .open(ConfirmationDialogComponent, {
        data: { title: 'Are you sure?', description, confirmText, cancelText: 'Cancel' },
      })
      .afterClosed()
      .pipe(
        tap((confirmed) => {
          if (!confirmed) row.stateControl.setValue(previousValue);
        }),
        filter(Boolean),
        switchMap(() => this.runStateChange(row, requestedValue)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          row.widget.isLive = isActivating;
          this.toastService.showToast({
            mode: 'success',
            message: isActivating ? 'Standalone widget is now live' : 'Standalone widget disabled',
          });
        },
        error: (err) => {
          console.error('Failed to change standalone widget state:', err);
          this.toastService.showToast({ mode: 'danger', message: 'Failed to change widget state' });
          row.stateControl.setValue(previousValue);
        },
      });
  }

  private runStateChange(row: StandaloneWidgetRow, nextValue: StandaloneMode): Observable<StandaloneWidgetItem> {
    return nextValue === 'live'
      ? this.smartCartService.activateStandaloneWidget(row.widget.id)
      : this.smartCartService.deactivateStandaloneWidget(row.widget.id);
  }
}
